import React, { createContext, useContext, useEffect, useState } from 'react';
import Cookies from 'universal-cookie';

const cookies = new Cookies();

const LOCAL_MODE = false;

function apiUrl(path: string): string {
  return LOCAL_MODE ? `http://localhost:8000${path}` : path;
}

function credentials(): RequestCredentials {
  return LOCAL_MODE ? 'include' : 'same-origin';
}

// Parses a JSON response, or throws an error naming the URL the response
// actually came from (after redirects), its status and its content type.
async function readJson(res: Response): Promise<any> {
  const type = res.headers.get('content-type') || '';
  if (!type.includes('application/json')) {
    throw new Error(`Expected JSON from ${res.url} but got ${res.status} ${type || '(no content type)'}`);
  }
  return res.json();
}

const fetchCsrfToken = async (): Promise<string | null> => {
  try {
    const response = await fetch(apiUrl('/api/csrf-token/'), {
      method: 'GET',
      credentials: credentials(),
    });
    if (response.ok) {
      const data = await readJson(response);
      return data.csrfToken ?? null;
    }
    console.error(`CSRF token request to ${response.url} failed with ${response.status}`);
  } catch (err) {
    console.error('Failed to fetch CSRF token', err);
  }
  return null;
};

// Result of the email-code endpoints. `detail` is a message safe to show the user.
export type AuthResult = { ok: boolean; detail: string };

interface AuthContextType {
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  organization: string;
  organization_id: number;
  ps_id: number;
  plan: string;
  organization_permissions: string[];
  isAuthenticated: boolean;
  // False until the first session check has finished, so pages can wait
  // instead of briefly showing the signed-out view.
  sessionChecked: boolean;
  refreshSession: () => Promise<void>;
  department: string; // ✅ NEW
  is_staff: boolean;
  coverage_area: string;
  error: string;
  clearError: () => void;
  getPermissions: () => Promise<{ groups: Record<string, any> }>;
  getDashboardPermissions: () => Promise<{ groups: Record<string, any> }>;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  whoami: () => Promise<void>;
  // Login with emailed code
  requestLoginCode: (email: string) => Promise<AuthResult>;
  loginWithCode: (email: string, code: string) => Promise<AuthResult>;
  // Password reset with emailed code
  requestPasswordReset: (email: string) => Promise<AuthResult>;
  confirmPasswordReset: (email: string, code: string, newPassword: string) => Promise<AuthResult>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [username, setUsername] = useState('');
  const [first_name, setFirstName] = useState('');
  const [last_name, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [organization, setOrganization] = useState('');
  const [organization_id, setOrganization_id] = useState(-1);
  const [ps_id, setPs_id] = useState(-1);
  const [plan, setPlan] = useState('');
  const [organizationPermissions, setOrganizationPermissions] = useState<string[]>([]);
  const [coverage_area, setCoverageArea] = useState('');
  const [error, setError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [department, setDepartment] = useState('');
  const [is_staff, setIs_staff] = useState(false);

  useEffect(() => {
    getSession();
  }, []);

  const getSession = async () => {
    try {
      const res = await fetch(apiUrl('/portal/user_session/'), {
        method: 'GET',
        credentials: credentials(),
        cache: 'no-store', // never reuse an old signed-in/out answer
      });

      if (!res.ok) throw new Error(`Primary session fetch to ${res.url} failed with ${res.status}`);

      const data = await readJson(res);
      setIsAuthenticated(data.isauthenticated);
      setUsername(data.username);
      setFirstName(data.first_name);
      setLastName(data.last_name);
      setEmail(data.email);
      setOrganization(data.organization); // ✅
      setOrganization_id(data.organization_id); // ✅
      setPs_id(data.ps_id); // ✅
      setPlan(data.plan); // ✅
      setOrganizationPermissions(data.organization_permissions || []); // ✅
      setCoverageArea(data.coverage_area);
      setIs_staff(data.is_staff);
      setDepartment(data.department);
    } catch (err) {
      console.warn('Primary session fetch failed, trying fallback...', err);

      try {
        const fallbackRes = await fetch(apiUrl('/apidet/session/'), {
          method: 'GET',
          credentials: credentials(),
          cache: 'no-store',
        });

        if (!fallbackRes.ok) throw new Error(`Fallback session fetch to ${fallbackRes.url} failed with ${fallbackRes.status}`);

        const data = await readJson(fallbackRes);
        setIsAuthenticated(data.isauthenticated);
        setUsername(data.username);
        setFirstName(data.first_name);
        setLastName(data.last_name);
        // Email, org, etc. might not be available from fallback
      } catch (fallbackErr) {
        console.error('Both session fetch attempts failed:', fallbackErr);
      }
    } finally {
      setSessionChecked(true);
    }
  };

  const getPermissions = async () => {
    try {
      const res = await fetch(apiUrl('/apidet/permissions/'), {
        method: 'GET',
        credentials: credentials(),
      });
      const data = await res.json();
      setIsAuthenticated(data.isauthenticated);
      return data.groups;
    } catch (err) {
      console.error(err);
    }
  };

  const getDashboardPermissions = async () => {
    try {
      const res = await fetch(apiUrl('/apidet/dashboard_permissions/'), {
        method: 'GET',
        credentials: credentials(),
      });
      const data = await res.json();
      setIsAuthenticated(data.isauthenticated);
      return data.groups;
    } catch (err) {
      console.error(err);
    }
  };

  const whoami = async () => {
    try {
      const res = await fetch(apiUrl('/apidet/whoami/'), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: credentials(),
      });
      const data = await res.json();
      setUsername(data.username);
    } catch (err) {
      console.error(err);
    }
  };

  const getCsrfToken = (): string | null => {
    const value = document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute('content');
    // On the React dev server index.html isn't rendered by Django, so the tag
    // holds the literal "{{ csrf_token }}". Treat that as no token.
    return value && !value.includes('{{') ? value : null;
  };

  // Cookie first (Django rotates it on login), then the meta tag, then ask Django.
  const resolveCsrfToken = async (): Promise<string | null> =>
    cookies.get('csrftoken') || getCsrfToken() || (await fetchCsrfToken());

  // Shared POST helper for the auth endpoints. Never throws.
  const postAuth = async (path: string, body: object): Promise<AuthResult> => {
    const csrfToken = await resolveCsrfToken();
    if (!csrfToken) {
      console.error('CSRF token not found');
      return { ok: false, detail: 'Your session has expired. Refresh the page and try again.' };
    }

    try {
      const res = await fetch(apiUrl(path), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: credentials(),
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));
      let detail = '';
      if (typeof data.detail === 'string') detail = data.detail;
      else if (Array.isArray(data.detail)) detail = data.detail.join(' '); // password validation errors
      else if (!res.ok) detail = 'Something went wrong. Try again.';

      return { ok: res.ok, detail };
    } catch (err) {
      console.error(err);
      return { ok: false, detail: "Couldn't reach the server. Check your connection and try again." };
    }
  };

  const clearError = () => setError('');

  const login = async (username: string, password: string) => {
    if (!username || !password) {
      setError('Enter your username and password');
      return;
    }

    const result = await postAuth('/apidet/login/', { username, password });
    if (!result.ok) {
      setError(
        result.detail === 'Invalid credentials' ? 'Wrong username or password' : result.detail
      );
      return;
    }

    setIsAuthenticated(true);
    setUsername(username);
    setError('');

    // Fetch session data after login to update first_name
    await getSession();
  };

  // ── Login with emailed code ────────────────────────────────────────────
  const requestLoginCode = (email: string) =>
    postAuth('/apidet/login/code/request/', { email });

  const loginWithCode = async (email: string, code: string): Promise<AuthResult> => {
    const result = await postAuth('/apidet/login/code/verify/', { email, code });
    if (!result.ok) return result;

    // Django has set the session cookie; load the user the same way password login does.
    setError('');
    await getSession();
    return result;
  };

  // ── Password reset with emailed code ───────────────────────────────────
  const requestPasswordReset = (email: string) =>
    postAuth('/apidet/password-reset/request/', { email });

  const confirmPasswordReset = (email: string, code: string, newPassword: string) =>
    postAuth('/apidet/password-reset/confirm/', { email, code, new_password: newPassword });

  const logout = async () => {
    try {
      const res = await fetch(apiUrl('/apidet/logout/'), {
        method: 'GET',
        credentials: credentials(),
      });
      if (!res.ok) throw new Error('Logout failed');
      localStorage.removeItem("selectedGroup"); // Clear the stored group
      setIsAuthenticated(false);
      setUsername('');
      setFirstName('');
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        username,
        first_name,
        last_name,
        email,
        organization, // ✅
        organization_id,
        ps_id,
        plan, // ✅
        organization_permissions: organizationPermissions, // ✅
        isAuthenticated,
        sessionChecked,
        refreshSession: getSession,
        coverage_area,
        department,
        is_staff,
        error,
        clearError,
        getPermissions,
        getDashboardPermissions,
        login,
        logout,
        whoami,
        requestLoginCode,
        loginWithCode,
        requestPasswordReset,
        confirmPasswordReset,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};